import React, { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@ais-ui/ui-core';
import { ArrowUpDown } from 'lucide-react';

import { cn } from '@ais-ui/utils';
import { 
  useTableDensity, 
  TableDensityClasses,
  TruncatedCell,
  DateTimeCell,
  NumericCell,
  ShortTextCell,
} from '../../../utils/useTableDensity';
import { myCustomFilter } from '../filterTypes';

/**
 * Configuration for table density - can be provided externally for popup usage
 */
export interface TableDensityConfig {
  tableDensity: 'normal' | 'compact';
  densityClasses: TableDensityClasses;
}

/**
 * Core column creation logic - can be used with any density config
 */
export function createColumns(
  accessorKeys: string[],
  formatDateTimeWithTimeZone: (date: Date | undefined | null) => string,
  densityConfig: TableDensityConfig,
): ColumnDef<any>[] {
  const { tableDensity, densityClasses } = densityConfig;

  return accessorKeys.map((accessorKey) => {
    switch (accessorKey.toLowerCase()) {
        case 'flag':
        case 'to flag':
        case 'from flag':
          return {
            accessorKey,
            size: 70,
            maxSize: 80,
            header: ({ column }: { column: any }) => (
              <Button
                variant="ghost"
                className={cn(
                  'px-0 w-full flex justify-between items-center gap-1',
                  densityClasses.text,
                )}
                onClick={() =>
                  column.toggleSorting(column.getIsSorted() === 'asc')
                }
              >
                <p className="truncate">{accessorKey}</p>
                <ArrowUpDown
                  className={cn('flex-shrink-0', densityClasses.icon.small)}
                />
              </Button>
            ),
            cell: ({ getValue }: { getValue: any }) => (
              <div className={densityClasses.cellContent}>
                <img
                  src={getValue() as string}
                  alt="Flag"
                  className={cn(
                    'object-contain',
                    tableDensity === 'compact' ? 'max-h-4' : 'max-h-6',
                  )}
                />
              </div>
            ),
          };

        case 'distance':
        case 'length':
        case 'width':
        case 'draught':
          return {
            accessorKey,
            size: 80,
            maxSize: 100,
            header: ({ column }: { column: any }) => (
              <Button
                variant="ghost"
                className={cn(
                  'px-0 w-full flex justify-between items-center gap-1',
                  densityClasses.text,
                )}
                onClick={() =>
                  column.toggleSorting(column.getIsSorted() === 'asc')
                }
              >
                <p className="truncate">{accessorKey}</p>
                <ArrowUpDown
                  className={cn('flex-shrink-0', densityClasses.icon.small)}
                />
              </Button>
            ),
            cell: ({ getValue }: { getValue: any }) => {
              const value = getValue();
              const numValue = Number(value);
              const displayValue = !isNaN(numValue)
                ? numValue.toFixed(2)
                : value;

              return (
                <NumericCell className={densityClasses.cellContent}>
                  {displayValue}
                </NumericCell>
              );
            },
            accessorFn: (row: any) => {
              const value = row[accessorKey];
              const numValue = Number(value);
              return !isNaN(numValue) ? numValue.toFixed(2) : value;
            },
            filterFn: myCustomFilter,
          };

        case 'begin':
        case 'end':
        case 'enter':
        case 'exit':
        case 'timestamp':
        case 'ata':
        case 'atd':
        case 'sta':
        case 'std':
        case 'date':
        case 'arrival':
        case 'departure':
        case 'dats':
        case 'act':
        case 'dea':
        case 'edats':
        case 'sdats':
        case 'end date':
        case 'start date':
        case 'cpat':
        case 'actts':
        case 'deats':
        case 'eta':
          return {
            accessorKey,
            size: 180,
            maxSize: 200,
            header: ({ column }: { column: any }) => (
              <Button
                variant="ghost"
                className={cn(
                  'px-0 w-full flex justify-between items-center gap-1',
                  densityClasses.text,
                )}
                onClick={() =>
                  column.toggleSorting(column.getIsSorted() === 'asc')
                }
              >
                <p className="truncate">{accessorKey}</p>
                <ArrowUpDown
                  className={cn('flex-shrink-0', densityClasses.icon.small)}
                />
              </Button>
            ),
            cell: ({ getValue }: { getValue: any }) => {
              const formattedDate = formatDateTimeWithTimeZone(
                getValue() as Date,
              );

              return (
                <DateTimeCell className={densityClasses.cellContent}>
                  {formattedDate}
                </DateTimeCell>
              );
            },
          };

        case 'period':
          return {
            accessorKey,
            size: 70,
            maxSize: 90,
            header: ({ column }: { column: any }) => (
              <Button
                variant="ghost"
                className={cn(
                  'px-0 w-full flex justify-between items-center gap-1',
                  densityClasses.text,
                )}
                onClick={() =>
                  column.toggleSorting(column.getIsSorted() === 'asc')
                }
              >
                <p className="truncate">{accessorKey}</p>
                <ArrowUpDown
                  className={cn('flex-shrink-0', densityClasses.icon.small)}
                />
              </Button>
            ),
            cell: ({ getValue }: { getValue: any }) => {
              const value = String(getValue() || '');
              return (
                <ShortTextCell className={densityClasses.cellContent}>
                  {value}
                </ShortTextCell>
              );
            },
            accessorFn: (row: any) => row[accessorKey],
            filterFn: myCustomFilter,
          };

        case 'imo':
        case 'mmsi':
        case 'callsign':
        case 'call sign':
          return {
            accessorKey,
            size: 100,
            maxSize: 120,
            header: ({ column }: { column: any }) => (
              <Button
                variant="ghost"
                className={cn(
                  'px-0 w-full flex justify-between items-center gap-1',
                  densityClasses.text,
                )}
                onClick={() =>
                  column.toggleSorting(column.getIsSorted() === 'asc')
                }
              >
                <p className="truncate">{accessorKey}</p>
                <ArrowUpDown
                  className={cn('flex-shrink-0', densityClasses.icon.small)}
                />
              </Button>
            ),
            cell: ({ getValue }: { getValue: any }) => {
              const value = String(getValue() || '');
              return (
                <ShortTextCell className={densityClasses.cellContent}>
                  {value}
                </ShortTextCell>
              );
            },
            accessorFn: (row: any) => row[accessorKey],
            filterFn: myCustomFilter,
          };

        case 'desc':
        case 'description':
          return {
            accessorKey,
            size: 200,
            maxSize: 300,
            header: ({ column }: { column: any }) => (
              <Button
                variant="ghost"
                className={cn(
                  'px-0 w-full flex justify-between items-center gap-1',
                  densityClasses.text,
                )}
                onClick={() =>
                  column.toggleSorting(column.getIsSorted() === 'asc')
                }
              >
                <p className="truncate">{accessorKey}</p>
                <ArrowUpDown
                  className={cn('flex-shrink-0', densityClasses.icon.small)}
                />
              </Button>
            ),
            cell: ({ getValue }: { getValue: any }) => {
              const value = String(getValue() || '');

              return (
                <TruncatedCell
                  text={value}
                  className={densityClasses.cellContent}
                >
                  {value}
                </TruncatedCell>
              );
            },
            accessorFn: (row: any) => row[accessorKey],
            filterFn: myCustomFilter,
          };

        case 'name':
        case 'ship name':
        case 'destination':
        case 'port name':
          return {
            accessorKey,
            size: 150,
            maxSize: 250,
            header: ({ column }: { column: any }) => (
              <Button
                variant="ghost"
                className={cn(
                  'px-0 w-full flex justify-between items-center gap-1',
                  densityClasses.text,
                )}
                onClick={() =>
                  column.toggleSorting(column.getIsSorted() === 'asc')
                }
              >
                <p className="truncate">{accessorKey}</p>
                <ArrowUpDown
                  className={cn('flex-shrink-0', densityClasses.icon.small)}
                />
              </Button>
            ),
            cell: ({ getValue }: { getValue: any }) => {
              const value = String(getValue() || '');

              return (
                <TruncatedCell
                  text={value}
                  className={densityClasses.cellContent}
                >
                  {value}
                </TruncatedCell>
              );
            },
            accessorFn: (row: any) => row[accessorKey],
            filterFn: myCustomFilter,
          };

        case 'route name':
        case 'departure locode':
        case 'arrival locode':
        case 'locode':
        case 'ship type':
          return {
            accessorKey,
            size: 120,
            maxSize: 180,
            header: ({ column }: { column: any }) => (
              <Button
                variant="ghost"
                className={cn(
                  'px-0 w-full flex justify-between items-center gap-1',
                  densityClasses.text,
                )}
                onClick={() =>
                  column.toggleSorting(column.getIsSorted() === 'asc')
                }
              >
                <p className="truncate">{accessorKey}</p>
                <ArrowUpDown
                  className={cn('flex-shrink-0', densityClasses.icon.small)}
                />
              </Button>
            ),
            cell: ({ getValue }: { getValue: any }) => {
              const value = String(getValue() || '');

              return (
                <TruncatedCell
                  text={value}
                  className={densityClasses.cellContent}
                >
                  {value}
                </TruncatedCell>
              );
            },
            accessorFn: (row: any) => row[accessorKey],
            filterFn: myCustomFilter,
          };

        case 'country':
          return {
            accessorKey,
            size: 100,
            maxSize: 150,
            header: ({ column }: { column: any }) => (
              <Button
                variant="ghost"
                className={cn(
                  'px-0 w-full flex justify-between items-center gap-1',
                  densityClasses.text,
                )}
                onClick={() =>
                  column.toggleSorting(column.getIsSorted() === 'asc')
                }
              >
                <p className="truncate">{accessorKey}</p>
                <ArrowUpDown
                  className={cn('flex-shrink-0', densityClasses.icon.small)}
                />
              </Button>
            ),
            cell: ({ getValue }: { getValue: any }) => {
              const value = String(getValue() || '');

              return (
                <TruncatedCell
                  text={value}
                  className={densityClasses.cellContent}
                >
                  {value}
                </TruncatedCell>
              );
            },
            accessorFn: (row: any) => row[accessorKey],
            filterFn: myCustomFilter,
          };

        default:
          return {
            accessorKey,
            size: 120,
            maxSize: 200,
            header: ({ column }: { column: any }) => (
              <Button
                variant="ghost"
                className={cn(
                  'px-0 w-full flex justify-between items-center gap-1',
                  densityClasses.text,
                )}
                onClick={() =>
                  column.toggleSorting(column.getIsSorted() === 'asc')
                }
              >
                <p className="truncate">{accessorKey}</p>
                <ArrowUpDown
                  className={cn('flex-shrink-0', densityClasses.icon.small)}
                />
              </Button>
            ),
            cell: ({ getValue }: { getValue: any }) => {
              const value = String(getValue() || '');

              return (
                <TruncatedCell
                  text={value}
                  className={densityClasses.cellContent}
                >
                  {value}
                </TruncatedCell>
              );
            },
            accessorFn: (row: any) => row[accessorKey],
            filterFn: myCustomFilter,
          };
      }
    });
}

/**
 * Hook version that uses useTableDensity from main app settings
 * This is the original hook for backwards compatibility
 */
export function useCreateColumns(
  accessorKeys: string[],
  formatDateTimeWithTimeZone: (date: Date | undefined | null) => string,
): ColumnDef<any>[] {
  const { tableDensity, densityClasses } = useTableDensity();

  return useMemo(() => {
    return createColumns(accessorKeys, formatDateTimeWithTimeZone, {
      tableDensity,
      densityClasses,
    });
  }, [accessorKeys, formatDateTimeWithTimeZone, tableDensity, densityClasses]);
}
